// @ts-nocheck
import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';
import classNames from 'classnames';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';

import applicationActions from 'common/support/applicationActions';

import { pageSelected } from 'actions/pageView';

import textToSpeechManager from 'support/tts/TextToSpeechManager';
import {
  getPageValues,
  createBreadCrumb,
} from 'support/analytics/analyticsHelper';
import { FocusGroup } from 'components/focus';
import LeftNav from 'components/navigation/LeftNav';
import { FocusSettingsProvider } from 'components/focus/FocusSettingsContext';
import WithLoading from 'components/common/WithLoading';

import { useFocusManager } from 'hooks/focus';
import { useRestoration } from 'hooks/restoration';
import { isPromoRow } from 'support/rowHelper';
import useTracking from 'hooks/analytics';
import LandingPageRows from './LandingPageRows';

import styles from './LandingPage.module.scss';

const messages = {
  homeTts: {
    id: 'home.tts.surfaceHome',
    description: 'Tts for when the home page is mounted',
  },
  settingsRowTitleTts: {
    id: 'tile.settings.tts.rowTitle',
    description: 'Settings tts for row title',
  },
  rectangleRowTitleTts: {
    id: 'tile.rectangle.tts.rowTitle',
    description: 'Rectangle tts for row title',
  },
  circleRowTitleTts: {
    id: 'tile.circle.tts.rowTitle',
    description: 'Circle tts for row title',
  },
};
const RestorationStatus = {
  RESTORED: 'restored',
  STARTED: 'started',
  UNRESTORED: 'unrestored',
};

const trackInteriorPage = (tracker, { content, pageType, historyStack }) => {
  const pageData = {
    pageType,
    ...getPageValues(content),
    breadCrumb: createBreadCrumb(historyStack),
  };
  tracker(pageData);
};

const useLandingTTS = (nowPlayingRef, footerRef, pageName) => {
  const focusManager = useFocusManager();
  const rows = useSelector(state => state.kitchenSink.data.rows);
  const intl = useIntl();

  const firstTtsCall = useRef(true);
  useEffect(() => {
    const onFocusChangedListener = (newId, prevId) => {
      const isRectangleRow = newId.includes('rectangle');
      const isCircleRow = newId.includes('circle');
      const focusedTTS = textToSpeechManager.getTTSText(newId);
      if (
        (!focusedTTS ||
          !focusManager.isFocusAncestor(`${pageName}-focusable-content`)) &&
        !isRectangleRow &&
        !isCircleRow
      )
        return;

      const getRowId = id => {
        // Check for null id. In an edge case, 'prevId' could be null if user click
        // a media tile before initial focus is set
        if (!id) return null;
        const regex = /\brow-([^-]+)/;
        const match = id.match(regex);
        return match?.[1];
      };

      if (firstTtsCall.current) {
        textToSpeechManager.add(intl.formatMessage(messages.homeTts));
        firstTtsCall.current = false;
      }

      if (newId.includes('firstrow') && !prevId.includes('firstrow')) {
        textToSpeechManager.add(nowPlayingRef.current?.getTtsText());
      }

      const newRowId = getRowId(newId);
      const prevRowId = getRowId(prevId);

      if (newRowId !== prevRowId) {
        if (isRectangleRow) {
          textToSpeechManager.add(
            intl.formatMessage(messages.leaguesRowTitleTts),
          );
        } else if (isCircleRow) {
          textToSpeechManager.add(
            intl.formatMessage(messages.circleRowTitleTts),
          );
        } else if (!newId.includes('firstrow')) {
          // We intentionally do not speak the row title for the first row
          // because the title is not rendered in the app
          const newRow = rows.find(row => row.id === newRowId);

          // If the title field of promo rows ever becomes useable,
          // we can remove the guard against promo rows
          if (!isPromoRow(newRow) && newRow?.title)
            textToSpeechManager.add(newRow.title);
        }
      }

      textToSpeechManager.add(focusedTTS);

      if (newId.includes('lastrow') && !prevId.includes('lastrow')) {
        textToSpeechManager.add(footerRef.current?.getTtsText());
      }

      textToSpeechManager.speak();
    };
    focusManager.addFocusChangedListener(onFocusChangedListener);
    return () => {
      focusManager.removeFocusChangedListener(onFocusChangedListener);
    };
  }, [focusManager, nowPlayingRef, footerRef, rows, intl, pageName]);

  return { enableTTSOnFocus: false };
};

const useLandingRestoration = (isReady, pageName) => {
  const { restorationState, setRestorationState } = useRestoration('landing');
  const focusManager = useFocusManager();

  const restoredFocusId = restorationState?.focusId;

  const [restorationStatus, setRestorationStatus] = useState(
    RestorationStatus.STARTED,
  );

  useEffect(() => {
    if (isReady && restorationStatus === RestorationStatus.STARTED) {
      if (
        restoredFocusId &&
        focusManager.getFocusableElement(restoredFocusId)
      ) {
        focusManager.setCurrentFocus(restoredFocusId);
        setRestorationStatus(RestorationStatus.RESTORED);
      } else {
        setRestorationStatus(RestorationStatus.UNRESTORED);
      }
    }
  }, [focusManager, isReady, restoredFocusId, restorationStatus]);

  useEffect(() => {
    const onFocusChangedListener = () => {
      if (focusManager.isFocusAncestor(`${pageName}-focusable-content`)) {
        setRestorationState({
          ...restorationState,
          focusId: focusManager.currentFocusId,
        });
      }
    };
    focusManager.addFocusChangedListener(onFocusChangedListener);
    return () => {
      focusManager.removeFocusChangedListener(onFocusChangedListener);
    };
  }, [focusManager, pageName, restorationState, setRestorationState]);

  return [restorationStatus, restoredFocusId];
};

const LandingPage = ({ pageType }) => {
  const { contentId } = useParams();
  const pageName = contentId;
  const dispatch = useDispatch();

  const nowPlayingRef = useRef();
  const footerRef = useRef();
  // useLandingTTS must be called before all other focus impacting hooks
  const { enableTTSOnFocus } = useLandingTTS(
    nowPlayingRef,
    footerRef,
    pageName,
  );

  const loading = useSelector(state => state.kitchenSink.loading);

  const focusManager = useFocusManager();
  const { trackPage: tracker } = useTracking();
  const history = useHistory();

  // Handles the initial focus setting logic
  const hasFocusBeenInitialized = useRef(false);
  const [restoredFocus] = useLandingRestoration(!loading, pageName);

  useEffect(() => {
    if (restoredFocus === RestorationStatus.UNRESTORED) {
      if (!hasFocusBeenInitialized.current) {
        focusManager.setCurrentFocus(
          `${pageName.toLowerCase()}-focusable-content`,
        );
        hasFocusBeenInitialized.current = true;
      }
    }
  }, [focusManager, restoredFocus, pageName]);

  useEffect(() => {
    dispatch(pageSelected(pageName, pageType));
  }, [dispatch, pageName, pageType]);

  useEffect(() => {
    if (restoredFocus === RestorationStatus.RESTORED) {
      hasFocusBeenInitialized.current = true;
    }
  }, [restoredFocus]);

  // Handles row data updating and re-setting focus appropriately
  const rowsLoading = useSelector(state => state.kitchenSink.rowsLoading);

  useEffect(() => {
    if (!rowsLoading && hasFocusBeenInitialized.current) {
      focusManager.setCurrentFocus('landing-rows');
    }
  }, [focusManager, rowsLoading]);

  useEffect(() => {
    trackInteriorPage(tracker, {
      content: contentId,
      pageType,
      historyStack: history.entries,
    });
  }, [tracker, contentId, pageType, history]);

  return (
    <WithLoading loading={loading}>
      <LeftNav focusId="left-nav-focusable-content" pageName={pageName} />
      <FocusSettingsProvider enableTTSOnFocus={enableTTSOnFocus}>
        {!loading && (
          <FocusGroup
            className="w-100 h-100 position-relative"
            focusId={`${pageName}-focusable-content`}
            onActionOverride={action => {
              if (action === applicationActions.back) {
                focusManager.setCurrentFocus('left-nav-focusable-content');
                return true;
              }
              return false;
            }}
          >
            <div
              className={classNames(
                'overflow-hidden',
                'background-gradient-page',
                'w-100 h-100',
                styles.padding,
              )}
            >
              <LandingPageRows
                focusId="landing-rows"
                trackingRowOffset={1}
                pageName={pageName}
              />
            </div>
          </FocusGroup>
        )}
      </FocusSettingsProvider>
    </WithLoading>
  );
};

LandingPage.propTypes = {
  pageType: PropTypes.string,
};

LandingPage.defaultProps = {
  pageType: null,
};

export default LandingPage;
